import { Button, Col, Form, Row } from "react-bootstrap";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRef, useState } from "react";
import axios from "axios";

import "./Login.css";
import { getAccountType, getHomePath } from "../utils/accountType";
import { storeUser } from "../redux/users/usersSlice";
import AuthBrandPanel from "../components/AuthBrandPanel";

const ACCENT = "#27b99a";
const BRAND = "#064260";
const MUTED = "#475569";
const SLATE = "#0f172a";

const Login = () => {
	const [errorMessage, setErrorMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const dispatch = useDispatch();
	const emailRef = useRef();
	const navigate = useNavigate();
	const passwordRef = useRef();

	const handleSubmit = async (event) => {
		event.preventDefault();
		const email = emailRef.current.value.trim();
		const password = passwordRef.current.value.trim();

		if (!email || !password) {
			setErrorMessage("Email and password are required.");
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/admin/login`, {
				email,
				password,
			});

			if (response.data.success === true) {
				const accountType = getAccountType(response.data) || "user";
				localStorage.setItem("accountType", accountType);
				localStorage.setItem(
					"businessId",
					response?.data?.businessId || response?.data?.organizationId || response?.data?.userId,
				);
				localStorage.setItem("businessIdentifier", response?.data?.businessIdentifier);
				localStorage.setItem("isFirstLogin", response?.data?.is_first_login);
				localStorage.setItem("organizationId", response?.data?.organizationId || response?.data?.userId || "");
				localStorage.setItem("permission", JSON.stringify(response?.data?.permission));
				localStorage.setItem("role", JSON.stringify(response?.data?.role));
				localStorage.setItem("user_id", response.data.token);
				localStorage.setItem("username", response.data.username);

				dispatch(storeUser({ user: { ...response.data, accountType } }));

				setErrorMessage("");

				if (response.data.is_first_login) {
					setTimeout(() => {
						navigate("/update-password");
					}, 1000);
				} else {
					setTimeout(() => {
						navigate(getHomePath());
					}, 1000);
				}
			} else {
				setErrorMessage("Invalid username or password. Please try again.");
			}
		} catch (error) {
			console.error("🚀 -> handleSubmit -> error:", error);

			if (!navigator.onLine) {
				setErrorMessage("Network connection lost. Please check your connection and try again.");
			} else {
				setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Row className="auth-page g-0 min-vh-100">
			<AuthBrandPanel />

			<Col
				className="d-flex align-items-center justify-content-center"
				lg={8}
				md={7}
				style={{ backgroundColor: "#f8fafc", padding: "60px" }}
				xs={12}>
				<Form className="w-100" onSubmit={handleSubmit} style={{ maxWidth: "400px" }}>
					<div className="d-flex flex-column mb-4" style={{ gap: "8px" }}>
						<h2 className="mb-0" style={{ color: SLATE, fontSize: "28px", fontWeight: 600 }}>
							Sign in to TeleTrace
						</h2>
						<p className="mb-0" style={{ color: MUTED, fontSize: "14px" }}>
							Enter your credentials below to access your workspace.
						</p>
					</div>

					<Form.Group className="mb-3" controlId="work-email">
						<Form.Label className="mb-2" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
							Work Email
						</Form.Label>
						<Form.Control
							autoComplete="email"
							className="auth-page__input"
							placeholder="name@organization.com"
							ref={emailRef}
							style={{
								backgroundColor: "#ffffff",
								border: "1px solid #cbd5e1",
								borderRadius: "8px",
								fontSize: "14px",
								padding: "12px",
							}}
							type="email"
						/>
					</Form.Group>

					<Form.Group className="mb-4" controlId="password">
						<div className="d-flex align-items-center justify-content-between mb-2">
							<Form.Label className="mb-0" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
								Password
							</Form.Label>
							<Link
								style={{
									color: ACCENT,
									fontSize: "14px",
									fontWeight: 500,
									textDecoration: "none",
								}}
								to="/forgot-password">
								Forgot password?
							</Link>
						</div>
						<div className="position-relative">
							<Form.Control
								autoComplete="current-password"
								className="auth-page__input"
								placeholder="••••••••••••"
								ref={passwordRef}
								style={{
									backgroundColor: "#ffffff",
									border: "1px solid #cbd5e1",
									borderRadius: "8px",
									fontSize: "14px",
									padding: "12px 40px 12px 12px",
								}}
								type={showPassword ? "text" : "password"}
							/>
							<Button
								aria-label={showPassword ? "Hide password" : "Show password"}
								className="position-absolute top-50 translate-middle-y border-0 bg-transparent p-0"
								onClick={() => setShowPassword(!showPassword)}
								style={{ color: MUTED, right: "12px" }}
								type="button"
								variant="link">
								{showPassword ? (
									<EyeOff color={MUTED} size={16} strokeWidth={2} />
								) : (
									<Eye color={MUTED} size={16} strokeWidth={2} />
								)}
							</Button>
						</div>
					</Form.Group>

					<div className="d-flex flex-column align-items-center" style={{ gap: "16px" }}>
						{errorMessage ? (
							<p
								className="mb-0 text-center w-100"
								role="alert"
								style={{ color: "#dc2626", fontSize: "14px", fontWeight: 500 }}>
								{errorMessage}
							</p>
						) : null}
						<Button
							className="w-100 border-0"
							disabled={isSubmitting}
							style={{
								backgroundColor: BRAND,
								borderRadius: "8px",
								fontSize: "16px",
								fontWeight: 600,
								padding: "14px",
							}}
							type="submit"
							variant="primary">
							{isSubmitting ? "Logging in..." : "Log In"}
						</Button>
						<p className="mb-0 text-center" style={{ color: MUTED, fontSize: "14px", fontWeight: 500 }}>
							Need assistance?{" "}
							<a
								href="mailto:support@teletrace-recovery.com"
								style={{ color: ACCENT, fontWeight: 600, textDecoration: "none" }}>
								Contact Support
							</a>
						</p>
					</div>
				</Form>
			</Col>
		</Row>
	);
};

export default Login;
